import { useState, useEffect } from 'react';
import OrderRtoManagement from '../../pages/OrderRtoManagement';
import OrderInstallationManagement from '../../pages/OrderInstallationManagement';
import OrderFinanceManagement from '../../pages/OrderFinanceManagement';
import OrderInsuranceManagement from '../../pages/OrderInsuranceManagement';
import OrderExchangeManagement from '../../pages/OrderExchangeManagement';
import OrderDeliveryManagement from '../../pages/delivery/OrderDeliveryManagement';

interface LogisticsModalProps {
    isOpen: boolean;
    onClose: () => void;
    order: any;
    onSuccess?: () => void;
    initialTab?: 'rto' | 'installation' | 'insurance' | 'finance' | 'exchange' | 'delivery' | null;
}

function LogisticsModal({ isOpen, onClose, order, onSuccess, initialTab }: LogisticsModalProps) {
    const [activeTab, setActiveTab] = useState<'rto' | 'installation' | 'insurance' | 'finance' | 'exchange' | 'delivery'>(
        (initialTab as any) || 'rto'
    );

    // Sync activeTab when modal is opened with a specific tab
    useEffect(() => {
        if (isOpen && initialTab) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    if (!isOpen || !order) return null;

    const handleSuccess = () => {
        if (onSuccess) onSuccess();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-6xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
                    <div className="px-10 py-8 border-b border-slate-100 flex items-center justify-between bg-white">
                        <div>
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Logistics Ecosystem</h2>
                            <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px] mt-1">Management Console • Order #{order.order_number}</p>
                        </div>
                        <button onClick={onClose} className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center text-slate-400 hover:bg-slate-900 hover:text-white transition-all duration-300">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    <div className="flex flex-col lg:flex-row min-h-[500px] max-h-[80vh]">
                        <div className="w-full lg:w-72 bg-slate-50/50 border-r border-slate-100 p-6 space-y-2 overflow-y-auto">
                            {[
                                { id: 'rto', label: 'RTO Registration', icon: '🏛️' },
                                { id: 'installation', label: 'Installation', icon: '🔧' },
                                { id: 'insurance', label: 'Insurance Policy', icon: '🛡️' },
                                { id: 'finance', label: 'Finance & EMI', icon: '💰' },
                                { id: 'exchange', label: 'Vehicle Exchange', icon: '🔄' },
                                { id: 'delivery', label: 'Delivery tracking', icon: '🚚' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`w-full p-4 rounded-2xl flex items-center gap-4 text-xs font-black uppercase tracking-widest transition-all duration-300 ${activeTab === tab.id ? 'bg-white border border-slate-200 text-indigo-600 shadow-xl shadow-slate-200/50' : 'text-slate-400 hover:text-slate-600 hover:bg-white/50'}`}
                                >
                                    <span className="text-xl">{tab.icon}</span>
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto bg-white p-6 lg:p-10">
                            {activeTab === 'rto' && <OrderRtoManagement orderIdProp={order.id} inModal={true} onSuccess={handleSuccess} />}
                            {activeTab === 'installation' && <OrderInstallationManagement orderIdProp={order.id} inModal={true} onSuccess={handleSuccess} />}
                            {activeTab === 'finance' && <OrderFinanceManagement orderIdProp={order.id} inModal={true} onSuccess={handleSuccess} />}
                            {activeTab === 'insurance' && <OrderInsuranceManagement orderIdProp={order.id} inModal={true} onSuccess={handleSuccess} />}
                            {activeTab === 'exchange' && <OrderExchangeManagement orderIdProp={order.id} inModal={true} onSuccess={handleSuccess} />}
                            {activeTab === 'delivery' && <OrderDeliveryManagement orderId={order.id} inModal={true} onSuccess={handleSuccess} />}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LogisticsModal;
